import React, { useMemo, useState } from 'react';
import i18n from '../../utilities/i18n';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import log from '../../utilities/log';
import Select from '../ui/forms/Select';
import TextInputWithError from '../ui/forms/TextInputWithError';
import { Button, VStack, Text, HStack } from '@adc/parallax-component-library';
import { Dialog } from 'tamagui';
import { Site, Subject } from '../../models';
import TextAreaWithError from '../ui/forms/TextAreaWithError';
import MessageBanner from '../messageBanner/MessageBanner';
import './Modal.scss';
import { subjectIdSchema } from 'adc-sirius-module-common';

type CreateSubjectModalProps = {
  studyKey: string;
  sites: Site[];
  trigger: React.ReactNode;
  onSuccess: (subject: Subject) => Promise<void>;
  subject?: Subject;
};

/**
 * Modal for creating a new subject.
 * @param {Site[]} sites - List of sites to select from.
 * @param {React.ReactNode} trigger - The element that triggers the modal.
 * @param {function} onSuccess - Callback function to be called when subject creation is successful
 * @param {Subject} subject - Optional subject object to pre-fill the form for editing.
 * @returns {React.JSX.Element}
 */
export default function CreateSubjectModal({
  sites,
  trigger,
  onSuccess,
  subject,
}: CreateSubjectModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const siteOptions = useMemo(() => {
    return sites.map((site) => ({
      name: site.name,
      id: site.key,
    }));
  }, [sites]);

  const initialValues: Partial<Subject> = {
    siteKey:
      subject?.siteKey || (siteOptions.length > 0 ? siteOptions[0].id : ''),
    name: subject?.name || '',
    description: subject?.description || '',
    type: subject?.type || '',
    region: subject?.region || '',
    systemNotes: [],
    userNotes: [],
  };

  const formSchema = Yup.object().shape({
    siteKey: Yup.string().required(i18n.t('Form.genericRequired')),
    name: Yup.string()
      .required(i18n.t('Form.genericRequired'))
      .matches(
        subjectIdSchema.matcher,
        i18n.t('Form.allowedSpecialCharacters', {
          characters: subjectIdSchema.allowedSpecialCharacters.join(' '),
        }),
      )
      .test(
        'len',
        () =>
          i18n.t('Form.genericMaxLength', { count: subjectIdSchema.maxLength }),
        (val) =>
          subjectIdSchema.maxLength && val
            ? val.length < subjectIdSchema.maxLength
            : true,
      ),
    description: Yup.string().optional(),
    type: Yup.string().optional(),
    region: Yup.string().optional(),
  });

  const onSubmit = async (
    values: Partial<Subject>,
    helpers: FormikHelpers<any>,
  ) =>
    onSuccess(values as Subject)
      .then(() => {
        helpers.resetForm();
        setIsOpen(false);
        setErrorMessage(null);
      })
      .catch((err) => {
        log.error(err);

        if (err.status === 409) {
          setErrorMessage(i18n.t('Modal_CreateSubject.error_duplicate'));
        } else {
          setErrorMessage(i18n.t('Modal_CreateSubject.error_generic'));
        }
      });

  return (
    <Dialog open={isOpen}>
      <Dialog.Trigger asChild onPress={() => setIsOpen(true)}>
        {trigger}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('Modal_CreateSubject.title')}
              </Text>
            </Dialog.Title>
            {errorMessage ? (
              <MessageBanner
                headerText={i18n.t('Modal_CreateSubject.error_header')}
                bodyText={errorMessage}
                type="danger"
                testID="error-banner"
              />
            ) : null}
          </VStack>
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<any>) => (
              <Form noValidate>
                <VStack paddingVertical="$4" gap="$2">
                  <TextInputWithError
                    label={i18n.t('Entity.name')}
                    name="name"
                    testID="name"
                  />
                  <TextAreaWithError
                    label={i18n.t('Entity.description')}
                    name="description"
                    testID="description"
                  />
                  <TextInputWithError
                    label={i18n.t('Entity.type')}
                    name="type"
                    testID="type"
                  />
                  <TextInputWithError
                    label={i18n.t('Entity.region')}
                    name="region"
                    testID="region"
                  />
                  <Select
                    id="siteKey"
                    name="siteKey"
                    label={i18n.t('Entity.site')}
                    options={siteOptions}
                    width="100%"
                    testID="site"
                  />
                </VStack>
                <HStack justifyContent="flex-end" gap="$2">
                  <Button
                    label={i18n.t('Common.cancel')}
                    variant="secondary"
                    disabled={formikProps.isSubmitting}
                    onPress={() => {
                      setIsOpen(false);
                      setErrorMessage(null);
                      formikProps.resetForm();
                    }}
                    testID="cancel-subject-creation"
                  />
                  <Button
                    label={i18n.t('Common.save')}
                    disabled={formikProps.isSubmitting || !formikProps.isValid}
                    onPress={formikProps.submitForm}
                    variant="primary"
                    type="submit"
                    testID="save-subject"
                  />
                </HStack>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
